/**
 * consent.ts (CONSENT-01, 360-RELOOK-01 R1, 2026-09-03) — the client side of
 * the one consent model (`contact_consent`). Every composer, the marketing
 * audience and the People & Lines drawer ask the same question here:
 *
 *   may we reach THIS line, on THIS channel, for THIS purpose?
 *
 * Answers: "allowed" (the default — Seth: everything on unless there is
 * proof), "unsubscribed" (a link click, ESC's Disable Emails, an office
 * do-not-contact) or "stop" (a texted STOP — only a texted START lifts it).
 * Rows are endpoint-level (one phone / one email) or customer-level ('*').
 * Writes go through the `contact_consent_set*` RPCs so normalization and the
 * STOP rule live in one place.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type ConsentChannel = "sms" | "email";
export type ConsentPurpose = "job_updates" | "billing" | "marketing";
export type ConsentStatus = "allowed" | "unsubscribed" | "stop";

export const PURPOSES: { key: ConsentPurpose; label: string; short: string }[] = [
  { key: "job_updates", label: "Job Updates", short: "Jobs" },
  { key: "billing", label: "Invoices & Receipts", short: "Invoices" },
  { key: "marketing", label: "Marketing", short: "Marketing" },
];

export interface ConsentRow {
  id: string;
  custno: string | null;
  channel: ConsentChannel;
  endpoint: string;
  purpose: ConsentPurpose;
  status: ConsentStatus;
  source: string;
  note: string | null;
  changed_by: string | null;
  changed_at: string;
}

/** Same normalization as the database: 10 digits for a phone, lowercase for an email. */
export function normEndpoint(channel: ConsentChannel, endpoint: string): string {
  if (channel === "sms") return endpoint.replace(/\D/g, "").slice(-10);
  return endpoint.trim().toLowerCase();
}

/** Which purpose a message type is sent under (mirrors send-email / podium-send). */
export function purposeForMessageType(messageType: string | null | undefined): ConsentPurpose {
  const t = (messageType ?? "").toLowerCase();
  if (t.startsWith("marketing")) return "marketing";
  if (["invoice", "statement", "payment-link", "receipt", "plan-welcome", "plan-renewal"].includes(t)) return "billing";
  return "job_updates";
}

/** Plain-English reason for a block, for the composers' captions. */
export function consentReason(row: ConsentRow | null | undefined): string | null {
  if (!row || row.status === "allowed") return null;
  const when = row.changed_at ? new Date(row.changed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
  if (row.status === "stop") return `texted STOP${when ? ` ${when}` : ""} — Pegasus won't text here`;
  switch (row.source) {
    case "esc": return "ESC: Disable Emails is on for this customer";
    case "unsubscribe_link": return `unsubscribed${when ? ` ${when}` : ""}`;
    case "office_do_not_text": return "on the office do-not-text list";
    case "office_pref": return "office: do not contact this way";
    default: return `unsubscribed${when ? ` ${when}` : ""} (${row.source})`;
  }
}

export const CONSENT_KEY = "contact_consent";

/**
 * MKT M6 (2026-09-03): marketing TEXTS need an opt-in ON RECORD — a row whose
 * source is one of these. "allowed" by default (no row) or a texted START
 * (which only lifts a STOP) is not an opt-in. Mirrors sms_marketing_optin().
 */
export const SMS_OPTIN_SOURCES = ["office_optin", "sms_keyword", "portal", "web_form", "import_optin"] as const;
export function isSmsMarketingOptin(row: ConsentRow | null | undefined): boolean {
  return !!row && row.status === "allowed" && (SMS_OPTIN_SOURCES as readonly string[]).includes(row.source);
}
/** Plain-English "how they opted in", for the pill tooltip. */
export function optinReason(row: ConsentRow | null | undefined): string | null {
  if (!isSmsMarketingOptin(row)) return null;
  const when = row!.changed_at ? new Date(row!.changed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  switch (row!.source) {
    case "office_optin": return `opted in — recorded by ${row!.changed_by?.split("@")[0] ?? "the office"}${when ? ` on ${when}` : ""}`;
    case "sms_keyword": return `opted in — texted OFFERS${when ? ` on ${when}` : ""}`;
    case "portal": return `opted in on the customer portal${when ? ` on ${when}` : ""}`;
    default: return `opted in (${row!.source})${when ? ` on ${when}` : ""}`;
  }
}

/**
 * Consent rows that can apply to a customer: the customer-level rows plus any
 * endpoint-level rows for the lines you pass in (lines can be shared or
 * unknown-sender rows without a custno, so we ask by endpoint too).
 */
export function useCustomerConsent(custno: string | null | undefined, lines: { channel: ConsentChannel; endpoint: string }[] = []) {
  const eps = [...new Set(lines.map((l) => normEndpoint(l.channel, l.endpoint)).filter((e) => e.length > 0))].sort();
  const q = useQuery({
    queryKey: [CONSENT_KEY, custno ?? null, eps],
    enabled: !!custno || eps.length > 0,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ConsentRow[]> => {
      const ors: string[] = [];
      if (custno) ors.push(`custno.eq.${custno}`);
      if (eps.length) ors.push(`endpoint.in.(${eps.map((e) => `"${e.replace(/"/g, "")}"`).join(",")})`);
      const { data, error } = await supabase.from("contact_consent").select("*").or(ors.join(","));
      if (error) throw error;
      return (data ?? []) as ConsentRow[];
    },
  });
  const rows = q.data ?? [];
  /** Resolve like the database does: endpoint row → customer row → allowed. */
  const rowFor = (channel: ConsentChannel, endpoint: string, purpose: ConsentPurpose): ConsentRow | null => {
    const ep = normEndpoint(channel, endpoint);
    const direct = rows.find((r) => r.channel === channel && r.endpoint === ep && r.purpose === purpose);
    if (direct) return direct;
    if (custno) {
      const cust = rows.find((r) => r.custno === custno && r.channel === channel && r.endpoint === "*" && r.purpose === purpose);
      if (cust) return cust;
    }
    return null;
  };
  const statusOf = (channel: ConsentChannel, endpoint: string, purpose: ConsentPurpose): ConsentStatus =>
    rowFor(channel, endpoint, purpose)?.status ?? "allowed";
  const blocked = (channel: ConsentChannel, endpoint: string, purpose: ConsentPurpose) => statusOf(channel, endpoint, purpose) !== "allowed";
  return { ...q, rows, rowFor, statusOf, blocked };
}

export function useSetConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      custno: string | null; channel: ConsentChannel; endpoint: string; purpose?: ConsentPurpose | "all";
      status: ConsentStatus; source?: string; note?: string | null;
    }) => {
      const purpose = v.purpose ?? "all";
      const { error } = purpose === "all"
        ? await supabase.rpc("contact_consent_set_all", { p_custno: v.custno, p_channel: v.channel, p_endpoint: v.endpoint, p_status: v.status, p_source: v.source ?? "office", p_note: v.note ?? null })
        : await supabase.rpc("contact_consent_set", { p_custno: v.custno, p_channel: v.channel, p_endpoint: v.endpoint, p_purpose: purpose, p_status: v.status, p_source: v.source ?? "office", p_note: v.note ?? null });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [CONSENT_KEY] }),
  });
}
